import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Pagination,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import { debounce } from 'lodash';

function MenuUsuarioList() {
  const navigate = useNavigate();

  // Filtros
  const [empresa, setEmpresa] = useState('');
  const [login, setLogin] = useState('');
  const [nome, setNome] = useState('');

  // Listagem e Paginação
  const [usuarios, setUsuarios] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [loading, setLoading] = useState(false);

  // Config
  const registrosPorPagina = 10;
  const API_URL = '/api/MenuUsuarios';

  // Função que chama o back-end
  const buscarUsuarios = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/buscar`, {
        params: {
          empresa,
          login,
          nome,
          page: pagina - 1, // zero-based
          size: registrosPorPagina
        }
      });
      setUsuarios(data.content);
      setTotalPaginas(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para evitar chamadas excessivas
  const debouncedBuscar = useCallback(debounce(() => {
    buscarUsuarios();
  }, 500), [empresa, login, nome, pagina]);

  // useEffect para disparar a busca automática
  useEffect(() => {
    debouncedBuscar();
    return debouncedBuscar.cancel;
  }, [empresa, login, nome, pagina, debouncedBuscar]);

  // Paginação
  const handleChangePagina = (event, value) => {
    setPagina(value);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setEmpresa('');
    setLogin('');
    setNome('');
    setPagina(1);
  };

  // Ações
  const handleNovo = () => {
    navigate('/menuusuario/edit'); // Ajuste conforme rota
  };

  const handleEditar = (idUsuario) => {
    navigate(`/menuusuario/edit/${idUsuario}`);
  };

  const handleExcluir = async (idUsuario) => {
    console.log('Excluindo usuário ID:', idUsuario);
    // Exemplo:
    // await axios.delete(`${API_URL}/${idUsuario}`);
    // buscarUsuarios();
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      {/* Backdrop Carregamento */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Título */}
      <Typography variant="h4" align="center" gutterBottom>
        Listagem de Usuários
      </Typography>

      {/* Card de Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Empresa"
                variant="outlined"
                size="small"
                fullWidth
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Login"
                variant="outlined"
                size="small"
                fullWidth
                value={login}
                onChange={(e) => setLogin(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Nome"
                variant="outlined"
                size="small"
                fullWidth
                value={nome}
                onChange={(e) => setNome(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <Button variant="outlined" onClick={limparFiltros} fullWidth>
                Limpar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Botão "+ Novo" */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleNovo}>
          + Novo
        </Button>
      </Box>

      {/* Tabela de Resultados */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Login</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Empresa</strong></TableCell>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum usuário encontrado.
                </TableCell>
              </TableRow>
            ) : (
              usuarios.map((u, index) => (
                <TableRow key={index}>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>{u.login}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.empresa}</TableCell>
                  <TableCell>{u.nome}</TableCell>
                  <TableCell>{u.status}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() => handleEditar(u.id)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleExcluir(u.id)}
                    >
                      Excluir
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPaginas}
          page={pagina}
          onChange={handleChangePagina}
          color="primary"
        />
      </Box>
    </Box>
  );
}

export default MenuUsuarioList;
